/**
 * Notification Toast System
 * 
 * Modern stacking toast notifications rendered in the bottom-right corner.
 * Non-intrusive, does not overlap any existing UI elements.
 * 
 * Features:
 * - Slide-in animation
 * - Auto-dismiss with progress bar
 * - Critical alerts require manual dismiss
 * - Severity-colored accent bar
 * - Max 3 visible, stacks vertically upward
 */

import { useEffect, useState } from 'react';
import { themeColor } from '../../styles/cssVars';
import { useNotifications } from '../../context/NotificationContext';
import type { Toast } from '../../context/NotificationContext';
import { formatTimeAgo } from '../../utils/formatTimeAgo';
import { useViewport } from '../../hooks/useViewport';

// ============================================================================
// Severity config
// ============================================================================

const SEVERITY_CONFIG = {
  info: {
    icon: 'ℹ️',
    tintCls: 'bg-info/[0.08]',
    accentCls: 'bg-info',
    colorKey: 'info' as const,
  },
  warning: {
    icon: '⚡',
    tintCls: 'bg-warning/[0.08]',
    accentCls: 'bg-warning',
    colorKey: 'warning' as const,
  },
  critical: {
    icon: '🔴',
    tintCls: 'bg-danger/[0.08]',
    accentCls: 'bg-danger',
    colorKey: 'danger' as const,
  },
};

// ============================================================================
// Component
// ============================================================================

export function NotificationToastContainer() {
  const { toasts, dismissToast, acknowledgeNotification } = useNotifications();
  const { isCompact } = useViewport();

  // Auto-dismiss ticking lives here (leaf) instead of the provider so the
  // notification context isn't invalidated every second. Only fires
  // dismissToast when a toast has actually expired.
  useEffect(() => {
    if (toasts.length === 0) return;
    const interval = setInterval(() => {
      const now = Date.now();
      for (const t of toasts) {
        if (t.dismissAt !== Infinity && now >= t.dismissAt) {
          dismissToast(t.id);
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [toasts, dismissToast]);

  // Toasts are desktop-only — on phone/tablet the Alerts page + banner cover delivery.
  if (isCompact || toasts.length === 0) return null;

  return (
    // bottom-24 keeps the stack clear of the FloatingChatWidget button (bottom-right corner)
    <div className="fixed bottom-24 right-6 z-[110] flex flex-col-reverse gap-2 max-w-[380px] pointer-events-none">
      {toasts.map((toast) => (
        <ToastCard
          key={toast.id}
          toast={toast}
          onDismiss={() => dismissToast(toast.id)}
          onAcknowledge={() => acknowledgeNotification(toast.notification.id)}
        />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss, onAcknowledge }: {
  toast: Toast;
  onDismiss: () => void;
  onAcknowledge: () => void;
}) {
  const [progress, setProgress] = useState(100);
  const [slideIn, setSlideIn] = useState(true);
  const { notification } = toast;
  const config = SEVERITY_CONFIG[notification.severity] || SEVERITY_CONFIG.info;
  const accentColor = themeColor[config.colorKey];
  const isCritical = notification.severity === 'critical';

  useEffect(() => {
    const timer = setTimeout(() => setSlideIn(false), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isCritical || toast.dismissAt === Infinity) return;
    
    const totalDuration = toast.dismissAt - toast.createdAt;
    const interval = setInterval(() => {
      const remaining = toast.dismissAt - Date.now();
      const pct = Math.max(0, (remaining / totalDuration) * 100);
      setProgress(pct);
    }, 100);

    return () => clearInterval(interval);
  }, [toast.dismissAt, toast.createdAt, isCritical]);

  return (
    <div
      className={`
        pointer-events-auto bg-surface/90 backdrop-blur-xl border border-border rounded-2xl
        overflow-hidden min-w-[320px] max-w-[380px]
        transition-[transform,opacity] duration-300
        ease-[cubic-bezier(0.34,1.56,0.64,1)]
        ${slideIn ? 'translate-x-[120%] opacity-0' : 'translate-x-0 opacity-100'}
      `}
      style={{
        boxShadow: `var(--ev-shadow-toast), 0 0 0 1px ${accentColor}22, var(--shadow-card)`,
      }}
    >
      {/* Accent bar + content */}
      <div className={`flex ${config.tintCls}`}>
        {/* Left accent bar */}
        <div className={`w-1 shrink-0 ${config.accentCls}`} />

        {/* Content */}
        <div className="flex-1 min-w-0 px-4 pt-3.5 pb-3">
          {/* Header row */}
          <div className="flex items-start gap-2.5 mb-1.5">
            <span className="text-base shrink-0 leading-none mt-0.5">{config.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-text leading-snug truncate">
                {notification.title}
              </div>
              <div className="text-xs text-text-muted leading-relaxed mt-1 line-clamp-2">
                {notification.message}
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={onDismiss}
              className="bg-transparent border-none text-text-dim cursor-pointer p-1 text-sm leading-none shrink-0 rounded-lg transition-colors duration-150 hover:text-text hover:bg-surface-active tap:min-h-12 tap:min-w-12"
            >
              ✕
            </button>
          </div>

          {/* Actions for critical */}
          {isCritical && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={onAcknowledge}
                className={`tap:min-h-12 px-4 py-1.5 text-xs font-semibold text-background border-none rounded-lg cursor-pointer transition-opacity duration-150 hover:opacity-85 ${config.accentCls}`}
              >
                Acknowledge
              </button>
              <button
                onClick={onDismiss}
                className="px-4 py-1.5 text-xs font-medium bg-transparent text-text-muted border border-border rounded-lg cursor-pointer hover:bg-surface-active transition-colors tap:min-h-12"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Source and time */}
          <div className="flex justify-between items-center mt-2 text-xs text-text-dim">
            <span>{notification.source}</span>
            <span>{formatTimeAgo(notification.timestamp)}</span>
          </div>
        </div>
      </div>

      {/* Auto-dismiss progress bar (non-critical only) */}
      {!isCritical && (
        <div className="h-0.5 bg-surface-active">
          <div
            className={`h-full opacity-60 transition-[width] duration-100 ease-linear ${config.accentCls}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default NotificationToastContainer;
